import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_PseudoClasses = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">✨ CSS Pseudo-Classes</h1>

      <p className="tutorial-intro">
        A <b>pseudo-class</b> is used to define the special state of an element.
        It allows you to style elements when the user interacts with them or
        when they meet certain conditions (like being the first child).
      </p>

      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          🌟 Why Use Pseudo-Classes?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Pseudo-classes let you apply styles dynamically without adding extra
          classes or JavaScript. Perfect for hover effects, active links,
          selecting first/last elements, and much more.
        </p>
      </div>
      {/* Common Uses of Pseudo-Classes */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          📘 CSS Pseudo-Classes
        </h2>
        <p>
          A <b>CSS pseudo-class</b> is a keyword that can be added to a selector
          to define a style for a <b>special state</b> of an element.
        </p>

        <h3 style={{ margin: "0.5rem 0", color: "#b45309" }}>
          ✅ Some common uses:
        </h3>
        <ul style={{ lineHeight: "1.8", marginLeft: "1.2rem" }}>
          <li>
            Style an element when a user <b>moves the mouse over it</b> (
            <code>:hover</code>)
          </li>
          <li>
            Style <b>visited</b> and <b>unvisited</b> links differently (
            <code>:visited</code>, <code>:link</code>)
          </li>
          <li>
            Style an element when it <b>gets focus</b> (<code>:focus</code>)
          </li>
          <li>
            Style <b>valid</b>, <b>invalid</b>, <b>required</b>, and{" "}
            <b>optional</b> form elements (<code>:valid</code>,{" "}
            <code>:invalid</code>, <code>:required</code>)
          </li>
          <li>
            Style an element that is the <b>first child</b> of its parent (
            <code>:first-child</code>)
          </li>
        </ul>
      </div>

      {/* 1. :hover */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ :hover</h2>
        <p>
          The <code>:hover</code> pseudo-class applies when the user moves the
          mouse pointer over an element.
        </p>

        <CodeCopyBlock
          code={`button:hover {
  background-color: #3b82f6;
  color: white;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <button
            style={{
              padding: "10px 20px",
              border: "2px solid #3b82f6",
              borderRadius: "6px",
              background: "white",
              cursor: "pointer",
            }}
          >
            Hover Me
          </button>
        </div>
      </div>

      {/* 2. :active */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ :active</h2>
        <p>
          The <code>:active</code> pseudo-class applies while an element is
          being clicked.
        </p>

        <CodeCopyBlock
          code={`a:active {
  color: red;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <a href="#" style={{ fontSize: "1.1rem" }}>
            Click this link
          </a>
        </div>
      </div>

      {/* 3. :focus */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ :focus</h2>
        <p>
          The <code>:focus</code> pseudo-class applies when an element (like an
          input) is focused (clicked or tabbed into).
        </p>

        <CodeCopyBlock
          code={`input:focus {
  border-color: #16a34a;
  outline: none;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <input
            type="text"
            placeholder="Click here"
            style={{
              padding: "8px",
              border: "2px solid #9ca3af",
              borderRadius: "6px",
            }}
          />
        </div>
      </div>

      {/* 4. :first-child */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ :first-child</h2>
        <p>
          The <code>:first-child</code> pseudo-class selects the first child of
          a parent element.
        </p>

        <CodeCopyBlock
          code={`p:first-child {
  color: blue;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <div>
            <p>First child (blue)</p>
            <p>Second child</p>
          </div>
        </div>
      </div>

      {/* 5. :last-child */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ :last-child</h2>
        <p>
          The <code>:last-child</code> pseudo-class selects the last child of a
          parent.
        </p>

        <CodeCopyBlock
          code={`p:last-child {
  color: green;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <div>
            <p>First child</p>
            <p>Last child (green)</p>
          </div>
        </div>
      </div>

      {/* 6. :nth-child(n) */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ :nth-child(n)</h2>
        <p>
          The <code>:nth-child()</code> pseudo-class selects elements based on
          their position.
        </p>

        <CodeCopyBlock
          code={`li:nth-child(2) {
  color: purple;
}`}
        />

        <ul style={{ marginTop: "1rem" }}>
          <li>Item 1</li>
          <li style={{ color: "purple" }}>Item 2 (styled)</li>
          <li>Item 3</li>
        </ul>
      </div>

      {/* Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          margin: "2rem 0",
        }}
      >
        <h2 style={{ color: "#92400e" }}>📌 Common Pseudo-Classes</h2>
        <ul style={{ lineHeight: "1.8" }}>
          <li>
            <b>:hover</b> → Mouse over
          </li>
          <li>
            <b>:active</b> → While clicking
          </li>
          <li>
            <b>:focus</b> → When focused
          </li>
          <li>
            <b>:first-child</b> / <b>:last-child</b> → Select first/last child
          </li>
          <li>
            <b>:nth-child(n)</b> → Select child by position
          </li>
          <li>
            <b>:not()</b> → Exclude specific elements
          </li>
          <li>
            <b>:checked</b> → For checkboxes/radios
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          After mastering pseudo-classes, check out{" "}
          <a href="/css/pseudo-elements" className="learn-more">
            CSS Pseudo-Elements
          </a>{" "}
          to style parts of an element (like first letter or before/after
          content).
        </p>
      </div>
    </div>
  );
};

export default CSS_PseudoClasses;
